/**
 * DataTable — Core configurable table using TanStack React Table.
 * Supports column resizing, sorting, and custom cell renderers.
 */
import React, { useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ArrowUpDown, GripVertical } from 'lucide-react';
import { renderCell } from './CellRenderers';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import { isBlankCustomValue, isTruthyCheckboxValue } from '../custom-fields/fieldTypes';

// #1000: the same rule as columnRegistry.isSortableColumn, read off the built column.
const isSortable = (col) => !col.computed && col.sortable !== false;

export default function DataTable({
    data = [],
    visibleColumns = [],
    sorting = [],
    onSortingChange,
    onRowClick,
    columnSizing = {},
    onColumnSizingChange,
    className = '',
    // Selection support
    selectable = false,
    selectedIds = new Set(),
    onSelectionChange,
    idField = 'id',
    // Optional row-actions column (#562) — trailing, non-sorting, non-resizing
    renderActions,
    // #1000: the rows are one server page, already in the requested order — the table
    // must not re-sort them (a re-sorted page 1 looks right while page 2 starts wrong).
    manualSorting = false,
    // #1000: optional bars rendered inside the card, above and below the scroll area
    // (the range / rows-per-page bar and the pager).
    header,
    footer,
}) {
    // Build TanStack column definitions from visible columns
    const columns = useMemo(() => {
        const cols = [];

        // Checkbox column for selection
        if (selectable) {
            const allIds = data.map(row => row[idField]).filter(Boolean);
            const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
            const someSelected = allIds.some(id => selectedIds.has(id)) && !allSelected;

            cols.push({
                id: '__select',
                header: () => (
                    <input
                        type="checkbox"
                        checked={allSelected}
                        ref={el => { if (el) el.indeterminate = someSelected; }}
                        aria-label="Select all rows on this page"
                        onChange={(e) => {
                            // #1000: only this page's rows change; picks made on other
                            // pages of a server-paged list stay selected.
                            const next = new Set(selectedIds);
                            allIds.forEach(id => (e.target.checked ? next.add(id) : next.delete(id)));
                            onSelectionChange?.(next);
                        }}
                        className="block w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20 cursor-pointer"
                    />
                ),
                cell: ({ row }) => {
                    const rowId = row.original[idField];
                    return (
                        <input
                            type="checkbox"
                            checked={selectedIds.has(rowId)}
                            onChange={(e) => {
                                e.stopPropagation();
                                const next = new Set(selectedIds);
                                if (e.target.checked) next.add(rowId);
                                else next.delete(rowId);
                                onSelectionChange?.(next);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20 cursor-pointer"
                        />
                    );
                },
                size: 40,
                enableSorting: false,
                enableResizing: false,
            });
        }

        cols.push(...visibleColumns.map(col => ({
            id: col.key,
            accessorFn: (row) => {
                // Handle custom fields
                // 0 and false are values (#972); only an unset field reads as empty.
                if (col.key.startsWith('cf_')) {
                    const value = row.custom_fields?.[col.key.slice(3)];
                    if (isBlankCustomValue(value)) return null;
                    // A Checkbox holds a real boolean or, from older imports, text like "false" / "no".
                    return col.type === 'checkbox' ? isTruthyCheckboxValue(value) : value;
                }
                // Handle computed contact name
                if (col.key === 'full_name') {
                    return `${row.first_name || ''} ${row.last_name || ''}`.trim();
                }
                return row[col.key];
            },
            header: ({ column }) => {
                // #1000: a computed or `sortable: false` column (columnRegistry.js) is a
                // muted label, not a button — the server cannot order every row by it.
                if (!isSortable(col)) {
                    return (
                        <span className="flex items-center gap-1 w-full" title="Not sortable" data-sortable="false">
                            <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider truncate">
                                {col.label}
                            </span>
                            {col.isCustom && <CustomFieldBadge />}
                        </span>
                    );
                }
                const isSorted = column.getIsSorted();
                return (
                    <button
                        className={`flex items-center gap-1 w-fit max-w-full text-left rounded -mx-1.5 -my-1 px-1.5 py-1 hover:bg-stone-200/60 ${isSorted ? 'bg-stone-200/60' : ''}`}
                        onClick={column.getToggleSortingHandler()}
                        title={isSorted === 'desc' ? 'Sorted descending · click for ascending'
                            : isSorted === 'asc' ? 'Sorted ascending · click to clear' : `Sort by ${col.label}`}
                        data-sortable="true"
                    >
                        <span className={`min-w-0 text-xs font-semibold uppercase tracking-wider truncate ${isSorted ? 'text-stone-900' : 'text-stone-600'}`}>
                            {col.label}
                        </span>
                        {col.isCustom && <CustomFieldBadge />}
                        <span aria-hidden="true">
                            {isSorted === 'asc' ? <ArrowUp className="w-3 h-3 text-stone-900" /> :
                             isSorted === 'desc' ? <ArrowDown className="w-3 h-3 text-stone-900" /> :
                             <ArrowUpDown className="w-3 h-3 text-stone-400" />}
                        </span>
                    </button>
                );
            },
            cell: ({ row, getValue }) => renderCell(col.type, getValue(), row.original, col),
            size: col.width || col.defaultWidth || 150,
            enableSorting: isSortable(col),
            enableResizing: true,
        })));
        // Row-actions column (#562) — rendered when the page passes
        // renderActions; an optional trailing column, other call sites
        // are unaffected.
        if (renderActions) {
            cols.push({
                id: '__actions',
                header: () => null,
                cell: ({ row }) => renderActions(row.original),
                size: 52,
                enableSorting: false,
                enableResizing: false,
            });
        }
        return cols;
    }, [visibleColumns, selectable, selectedIds, data, idField, onSelectionChange, renderActions]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnSizing,
        },
        onSortingChange,
        onColumnSizingChange,
        getCoreRowModel: getCoreRowModel(),
        ...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
    });

    const scrollArea = (
        <div className={header || footer ? 'overflow-auto' : `overflow-auto bg-white rounded-xl border border-stone-200 ${className}`}>
            <table className="w-full" style={{ minWidth: table.getTotalSize() }}>
                <thead className="sticky top-0 z-10 bg-stone-50 border-b border-stone-200">
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    className="relative px-3 py-2.5 text-left select-none"
                                    aria-sort={header.column.getIsSorted() === 'asc' ? 'ascending'
                                        : header.column.getIsSorted() === 'desc' ? 'descending' : undefined}
                                    style={{ width: header.getSize() }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}

                                    {/* Resize handle */}
                                    <div
                                        onMouseDown={header.getResizeHandler()}
                                        onTouchStart={header.getResizeHandler()}
                                        className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none
                                            hover:bg-stone-400 ${header.column.getIsResizing() ? 'bg-stone-500' : 'bg-transparent'}`}
                                    />
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center py-16 text-sm text-stone-400">
                                No records found
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map(row => (
                            <tr
                                key={row.id}
                                onClick={() => onRowClick?.(row.original)}
                                className={`border-b border-stone-100 hover:bg-stone-50 transition-colors cursor-pointer ${
                                    selectable && selectedIds.has(row.original[idField]) ? 'bg-stone-50' : ''
                                }`}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        className="px-3 py-2.5"
                                        style={{ width: cell.column.getSize(), maxWidth: cell.column.getSize() }}
                                    >
                                        <div className="truncate">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );

    if (!header && !footer) return scrollArea;
    return (
        <div className={`bg-white rounded-xl border border-stone-200 overflow-hidden ${className}`}>
            {header}
            {scrollArea}
            {footer}
        </div>
    );
}
